import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import { isObject } from '@iconik-io/core/src/is_object';

import { PlayerSyncErrorEvent } from '../../player_events';
import type { SyncAtom } from '../sync_atom';
import type { SyncMap } from '../sync_map';
import type { SyncStore } from '../sync_store';
import { validateSyncData } from '../validators';
import { SyncDataManager } from './data_manager';
import { ParticipantManager } from './participant_manager';
import type { SyncError } from './sync_error';
import {
  WebSocketClient,
  type WebSocketClientOptions,
} from './websocket_client';

const logger = DebugLogger.getInstance('unified_player_sync_client');

export type SyncSettings<T> = {
  syncClient: SyncClient;
  bootstrapStore?: T;
};

export type SyncData = {
  atom: string;
  action: SyncAction;
  value?: unknown;
  key?: string;
  last_set_timestamp?: number;
};

export type Message = SyncMessage | SetModeMessage;
export type SyncMessage = {
  command: 'SYNC' | 'BOOTSTRAP';
  command_id?: string;
  timestamp?: number;
  data?: SyncData[];
  error?: never;
};

export type SetModeMessage = {
  command: 'SET_MODE';
  command_id?: never;
  timestamp?: number;
  data: { client_id?: string; user_id?: string; mode: SyncMode };
  error?: SyncError;
};

export type SyncMode = 'ONLINE' | 'OFFLINE' | 'SYNC' | 'PRESENTATION';

export type SyncAction = 'SET' | 'UNSET' | 'SET_KEY' | 'UNSET_KEY';

export type ParticipantStatus = {
  userId: string;
  status: SyncMode;
};

type ConnectOptions = {
  serverUrl: string;
  systemName: string;
  systemDomainId: string;
  sessionId: string;
  userId: string;
};

/**
 * Main SyncClient class that coordinates the other components
 */
export class SyncClient {
  #client: WebSocketClient | undefined;
  #playerEvents: EventTarget;

  #participantManager: ParticipantManager;
  #dataManager: SyncDataManager;

  constructor(playerEvents: EventTarget) {
    this.#playerEvents = playerEvents;
    this.#participantManager = new ParticipantManager((message) =>
      this.#sendMessage(message),
    );
    this.#dataManager = new SyncDataManager((message) =>
      this.#sendMessage(message),
    );
  }

  #sendMessage(message: Message): void {
    logger.debug('Sending message', message);
    this.#client?.send(JSON.stringify(message));
  }

  #latestTimestamp = 0;

  #setLatestTimestamp(timestamp: number | undefined, force: boolean = false) {
    if (timestamp === undefined) {
      return;
    }
    if (force) {
      this.#latestTimestamp = timestamp;
      return;
    }

    const timestampIsContinuous =
      timestamp === this.#latestTimestamp + 1 ||
      timestamp === this.#latestTimestamp;

    if (timestampIsContinuous) {
      this.#latestTimestamp = timestamp;
      return;
    }
    logger.debug('Invalid timestamp', {
      expected: this.#latestTimestamp + 1,
      got: timestamp,
    });

    const currentSyncMode = this.#participantManager.$userSyncMode.get();

    if (currentSyncMode === 'SYNC') {
      this.#sendMessage({
        command: 'BOOTSTRAP',
      });
    }
  }

  #handleMessage(messageData: string): void {
    const message: Message = JSON.parse(messageData);
    const { command, command_id, timestamp, data, error } = message;
    logger.debug('Handle message', message);

    if (command === 'BOOTSTRAP') {
      this.#handleBootstrapMessage(command_id, data, timestamp);
      return;
    }
    if (this.#participantManager.$syncActive.get()) {
      this.#setLatestTimestamp(timestamp);
    }
    if (command === 'SYNC') {
      this.#handleSyncMessage(command_id, data);
    }

    if (command === 'SET_MODE') {
      this.#handleSetModeMessage(data, error);
    }
  }

  #handleSetModeMessage(
    data: SetModeMessage['data'],
    error: SetModeMessage['error'],
  ): void {
    const { client_id, user_id, mode } = data;

    if (error) {
      logger.debug('Error setting sync mode', error);
      this.#participantManager.setUserSyncMode(mode);
      this.#playerEvents.dispatchEvent(new PlayerSyncErrorEvent(error));
    }

    if (!client_id || !user_id) {
      logger.debug('Missing client_id or user_id in SetModeMessage');
      return;
    }
    this.#participantManager.updateParticipantStatus(client_id, user_id, mode);
  }

  #handleBootstrapMessage(
    commandId: string | undefined,
    data: SyncData[] | undefined,
    timestamp: number | undefined,
  ): void {
    // Bootstrap data accept, current client is a bootstrap consumer.
    if (data) {
      this.#dataManager.clear();
      this.#setLatestTimestamp(timestamp, true);
      this.#processSyncData(data);
      return;
    }

    // Bootstrap data send, current client is a bootstrap provider.
    const bootstrapData = this.#dataManager.getBootstrapData();
    const bootstrapMessage: SyncMessage = {
      command: 'BOOTSTRAP',
      command_id: commandId,
      data: bootstrapData,
      timestamp: this.#latestTimestamp,
    };
    this.#sendMessage(bootstrapMessage);
  }

  #handleSyncMessage(
    commandId: string | undefined,
    data: SyncData[] | undefined,
  ): void {
    if (!data) {
      this.#dataManager.handleSyncConfirmation(commandId);
      return;
    }

    this.#processSyncData(data);
  }

  #processSyncData(data: SyncData[]): void {
    this.#dataManager.setIsSyncing(true);

    data.forEach(
      ({ atom, action, value, key, last_set_timestamp: lastSetTimestamp }) => {
        const syncData: SyncData = {
          atom,
          action,
          last_set_timestamp: lastSetTimestamp,
        };

        if (value !== undefined) {
          syncData.value = value;
        }

        if (key !== undefined) {
          syncData.key = key;
        }

        if (import.meta.env.DEV && !validateSyncData(syncData)) {
          // biome-ignore lint/suspicious/noConsole: validation error
          console.error(
            `[Sync Receive Validation Failed] Invalid data for atom '${atom}':`,
            syncData,
          );
          return;
        }

        const store = this.#dataManager.syncStores.get(atom);
        if (!store) {
          // Store and merge unknown commands, for bootstrapping CineSync clients
          logger.debug(
            `No action handler found for sync atom: ${atom.toString()}`,
            // TODO: Remove this error once we the CineSync sync is fully implemented,
            //  until then it's useful for debugging (Because then it is an error)
          );

          this.#dataManager.storeUnknownData(syncData);
          return;
        }

        if ('setKey' in store && key !== undefined) {
          // If key is provided and store has setKey method, we use the setKey method
          if (
            lastSetTimestamp !== undefined &&
            lastSetTimestamp > store.lastSetTimestamp
          ) {
            return;
          }
          store.setKey(key, value);
        } else {
          // Otherwise treat as normal atom update
          if (lastSetTimestamp) {
            // Set the last set timestamp to the timestamp of the sync data
            // Local changes are timestamped in the set method on the store.
            store.setLastSetTimestamp(lastSetTimestamp);
          }
          if ('setKey' in store) {
            if (isObject(value)) {
              store.set(value);
            }
          } else {
            store.set(value);
          }
        }
      },
    );
    this.#dataManager.setIsSyncing(false);
  }

  connect({
    serverUrl,
    systemName,
    systemDomainId,
    sessionId,
    userId,
  }: ConnectOptions): void {
    const options: WebSocketClientOptions = {
      serverUrl,
      systemName,
      systemDomainId,
      sessionId,
      userId,
      onMessage: this.#handleMessage.bind(this),
      onOpen: this.handleOpen.bind(this),
      onClose: this.handleClose.bind(this),
    };

    logger.debug('Connecting to sync server', options);
    this.#client = WebSocketClient.connect(options);
  }

  setSyncMode(mode: SyncMode): void {
    this.#participantManager.setSyncMode(mode);
  }

  sync(store: SyncAtom | SyncMap, value: unknown, key?: string): void {
    this.#dataManager.queueSyncData(store, value, key);
  }

  handleOpen(): void {
    this.#dataManager.clear();
    const currentSyncMode = this.#participantManager.$userSyncMode.get();
    this.#participantManager.clear();

    if (currentSyncMode === 'OFFLINE') {
      this.setSyncMode('ONLINE');
      return;
    }
    this.setSyncMode(currentSyncMode);
  }

  handleClose(): void {
    this.#participantManager.clear();
    this.#dataManager.clear();
  }

  disconnect(): void {
    logger.debug('Disconnecting from sync server');
    this.#client?.disconnect();
    this.#client = undefined;
  }

  // Public access for deeper properties
  get syncStores(): Map<string, SyncStore> {
    return this.#dataManager.syncStores;
  }

  get $participantStatuses() {
    return this.#participantManager.$participantStatuses;
  }

  get $userSyncMode() {
    return this.#participantManager.$userSyncMode;
  }

  get $syncActive() {
    return this.#participantManager.$syncActive;
  }

  get $sessionSyncMode() {
    return this.#participantManager.$sessionSyncMode;
  }

  get isSyncing(): boolean {
    return this.#dataManager.isSyncing;
  }

  get latestTimestamp(): number {
    return this.#latestTimestamp;
  }
}
